import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api, { messagesAPI } from '../services/api';
import { onNewMessage, offEvent, getSocket } from '../services/socket';
import { usdt } from '../utils/currency';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingSignals, setPendingSignals] = useState(0);
  const [hasCapitalToInvest, setHasCapitalToInvest] = useState(false);
  const [walletNotifications, setWalletNotifications] = useState(0);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Charger les compteurs de notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        // Messages non lus
        const msgRes = await messagesAPI.getUnreadCount();
        setUnreadMessages(msgRes.data.unreadCount || 0);

        // Signaux en attente
        const sigRes = await api.get('/recommendations/pending');
        setPendingSignals(sigRes.data.recommendations?.length || 0);
      } catch (err) {
        // Silencieux
      }
    };

    loadNotifications();

    // Écouter les nouveaux messages
    const handleNewMessage = (data) => {
      if (data.userId === user?.id) {
        setUnreadMessages(prev => prev + 1);
      }
    };

    // Écouter les nouvelles suggestions
    const socket = getSocket();
    const handleNewSuggestions = () => {
      setPendingSignals(prev => prev + 1);
    };

    // Écouter les mises à jour de solde (dépôt/crédit admin)
    const handleBalanceUpdated = (data) => {
      if (data.userId === user?.id) {
        setWalletNotifications(prev => prev + 1);
        refreshUser(); // Mettre à jour le solde dans le header
      }
    };

    // Écouter les mises à jour de retrait
    const handleWithdrawalUpdated = (data) => {
      if (data.userId === user?.id) {
        setWalletNotifications(prev => prev + 1);
        refreshUser();
      }
    };

    // Écouter la validation/rejet de l'adresse de retrait par l'admin
    const handleWithdrawalAddressUpdated = (data) => {
      if (data.userId === user?.id) {
        setWalletNotifications(prev => prev + 1);
        refreshUser();
      }
    };

    // Écouter la validation/rejet d'un dépôt TXID par l'admin
    const handleDepositTxidUpdated = (data) => {
      if (data.userId === user?.id) {
        setWalletNotifications(prev => prev + 1);
        refreshUser();
      }
    };

    onNewMessage(handleNewMessage);
    if (socket) {
      socket.on('new_suggestions', handleNewSuggestions);
      socket.on('balance_updated', handleBalanceUpdated);
      socket.on('withdrawal_updated', handleWithdrawalUpdated);
      socket.on('withdrawal_address_updated', handleWithdrawalAddressUpdated);
      socket.on('deposit_txid_updated', handleDepositTxidUpdated);
    }

    return () => {
      offEvent('new_message_for_user');
      if (socket) {
        socket.off('new_suggestions', handleNewSuggestions);
        socket.off('balance_updated', handleBalanceUpdated);
        socket.off('withdrawal_updated', handleWithdrawalUpdated);
        socket.off('withdrawal_address_updated', handleWithdrawalAddressUpdated);
        socket.off('deposit_txid_updated', handleDepositTxidUpdated);
      }
    };
  }, [user?.id, refreshUser]);

  // Vérifier si l'utilisateur a du capital à investir
  useEffect(() => {
    if (user) {
      const balance = parseFloat(user.balance) || 0;
      setHasCapitalToInvest(balance > 0 && !user.capitalConfirmed);
    }
  }, [user]);

  // Réinitialiser le compteur quand on va sur la page messages
  useEffect(() => {
    if (location.search.includes('support')) {
      setUnreadMessages(0);
    }
  }, [location.search]);

  // Réinitialiser le compteur trading quand on va sur la page trading
  useEffect(() => {
    if (location.pathname === '/trading') {
      setPendingSignals(0);
    }
  }, [location.pathname]);

  // Réinitialiser le compteur wallet quand on clique sur l'onglet Historique
  useEffect(() => {
    const handleHistoryViewed = () => {
      setWalletNotifications(0);
    };

    window.addEventListener('wallet-history-viewed', handleHistoryViewed);
    return () => {
      window.removeEventListener('wallet-history-viewed', handleHistoryViewed);
    };
  }, []);

  const handleLogout = () => {
    setShowMenu(false);
    logout();
    navigate('/login');
  };

  const formatBalance = (balance) => usdt(balance);

  const getUserId = () => {
    if (user?.id) {
      return user.id.slice(0, 8).toUpperCase();
    }
    return '--------';
  };

  return (
    <div className="layout">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <div className="logo" onClick={() => navigate('/dashboard')}>
            <div className="logo-icon-wrapper">
              <span className="logo-symbol">₿</span>
            </div>
            <div className="logo-text">
              <span className="logo-name">CryptoBot</span>
              <span className="logo-tagline">Smart Trading</span>
            </div>
          </div>
        </div>

        <div className="header-right">
          {/* Balance */}
          <div className="header-balance" onClick={() => navigate('/wallet')}>
            <span className="hb-amount">{formatBalance(user?.balance)}</span>
            <span className="hb-label">{t('wallet.totalBalance')}</span>
          </div>

          {/* Profile */}
          <div className="profile-section">
            <div
              className="profile-trigger"
              onClick={() => setShowMenu(!showMenu)}
            >
              <div className="profile-avatar">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <svg
                className={`dropdown-arrow ${showMenu ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>

            {/* Dropdown Menu */}
            {showMenu && (
              <>
                <div className="menu-overlay" onClick={() => setShowMenu(false)}></div>
                <div className="profile-menu">
                  <div className="menu-header">
                    <div className="menu-avatar">
                      {user?.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="menu-user-info">
                      <span className="menu-username">{user?.username}</span>
                      <span className="menu-user-id">ID: {getUserId()}</span>
                    </div>
                  </div>

                  <div className="menu-divider"></div>

                  <div className="menu-items">
                    <button className="menu-item" onClick={() => { navigate('/profile'); setShowMenu(false); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <span>{t('profile.title')}</span>
                    </button>

                    <button className="menu-item" onClick={() => { navigate('/profile?section=referral'); setShowMenu(false); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      <span>{t('profile.referral')}</span>
                    </button>

                    <button className="menu-item" onClick={() => { navigate('/profile?section=help'); setShowMenu(false); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <span>{t('profile.helpContact')}</span>
                    </button>

                    <button className="menu-item" onClick={() => { navigate('/profile?section=settings'); setShowMenu(false); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                      <span>{t('profile.settings')}</span>
                    </button>
                  </div>

                  <div className="menu-divider"></div>

                  <button className="menu-item logout" onClick={handleLogout}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    <span>{t('auth.logout')}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <NavLink to="/dashboard" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <div className="nav-icon-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            {hasCapitalToInvest && (
              <span className="nav-badge capital">!</span>
            )}
          </div>
          <span>{t('nav.home')}</span>
        </NavLink>

        <NavLink to="/markets" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          <span>{t('nav.markets')}</span>
        </NavLink>

        <NavLink to="/trading" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <div className="nav-icon-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
            </svg>
            {pendingSignals > 0 && (
              <span className="nav-badge">{pendingSignals > 9 ? '9+' : pendingSignals}</span>
            )}
          </div>
          <span>{t('nav.trading')}</span>
        </NavLink>

        <NavLink to="/profile?section=support" className={({ isActive }) => `nav-item ${location.search.includes('support') ? 'active' : ''}`}>
          <div className="nav-icon-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {unreadMessages > 0 && (
              <span className="nav-badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
            )}
          </div>
          <span>{t('nav.messages')}</span>
        </NavLink>

        <NavLink to="/wallet" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <div className="nav-icon-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
              <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
            </svg>
            {walletNotifications > 0 && (
              <span className="nav-badge wallet">{walletNotifications > 9 ? '9+' : walletNotifications}</span>
            )}
          </div>
          <span>{t('nav.wallet')}</span>
        </NavLink>
      </nav>
    </div>
  );
};

export default Layout;
